import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, ChevronRight, X, ListTree, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { adminListCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function AdminCategories() {
  const { toast } = useToast();
  const { refreshPublic } = useApp();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [newCat, setNewCat] = useState({ name: "", icon: "Tag", image: "" });
  const [dlgOpen, setDlgOpen] = useState(false);
  const [newSub, setNewSub] = useState({});

  const reload = async () => {
    setLoading(true);
    try { setList(await adminListCategories()); } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!newCat.name.trim()) return;
    try {
      await adminCreateCategory(newCat);
      setNewCat({ name: "", icon: "Tag", image: "" });
      setDlgOpen(false);
      await reload();
      await refreshPublic();
      toast({ title: "Categorie adăugată" });
    } catch (err) { toast({ title: "Eroare", description: err?.response?.data?.detail || "" }); }
  };

  const del = async (id) => {
    if (!confirm("Șterge categoria?")) return;
    await adminDeleteCategory(id);
    await reload(); await refreshPublic();
    toast({ title: "Categorie ștearsă" });
  };

  const update = async (id, patch) => {
    await adminUpdateCategory(id, patch);
    await reload(); await refreshPublic();
  };

  const addSub = async (id) => {
    const name = (newSub[id] || "").trim();
    if (!name) return;
    const cat = list.find((c) => c.id === id);
    await adminUpdateCategory(id, { subcategories: [...(cat?.subcategories || []), name] });
    setNewSub((s) => ({ ...s, [id]: "" }));
    await reload(); await refreshPublic();
  };

  const removeSub = async (id, sub) => {
    const cat = list.find((c) => c.id === id);
    await adminUpdateCategory(id, { subcategories: (cat?.subcategories || []).filter((x) => x !== sub) });
    await reload(); await refreshPublic();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white flex items-center gap-2"><ListTree className="w-6 h-6" style={{ color: "var(--amd-primary)" }} /> Categorii & Subcategorii</h1>
          <p className="text-sm" style={{ color: "var(--amd-text-muted)" }}>{list.length} categorii — organizează structura site-ului.</p>
        </div>
        <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
          <DialogTrigger asChild>
            <Button className="amd-btn-primary gap-2"><Plus className="w-4 h-4" /> Adaugă categorie</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Categorie nouă</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label>Nume</Label><Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="amd-input h-11 mt-1" placeholder="Ex: Biciclete electrice" /></div>
              <div><Label>Icon (Lucide name)</Label><Input value={newCat.icon} onChange={(e) => setNewCat({ ...newCat, icon: e.target.value })} className="amd-input h-11 mt-1" placeholder="Tag" /></div>
              <div><Label>URL imagine</Label><Input value={newCat.image} onChange={(e) => setNewCat({ ...newCat, image: e.target.value })} className="amd-input h-11 mt-1" placeholder="https://…" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDlgOpen(false)} className="border-white/20 text-white hover:bg-white/5">Anulează</Button>
              <Button onClick={create} className="amd-btn-primary">Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="py-10 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /></div> : (
        <div className="grid gap-3">
          {list.map((c) => {
            const open = openId === c.id;
            return (
              <div key={c.id} className="amd-card rounded-lg">
                <div className="flex items-center justify-between gap-2 p-3">
                  <button onClick={() => setOpenId(open ? null : c.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} style={{ color: "var(--amd-text-muted)" }} />
                    {c.image ? <img src={c.image} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-white/5" />}
                    <div className="min-w-0">
                      <div className="font-bold text-white truncate">{c.name}</div>
                      <div className="text-xs" style={{ color: "var(--amd-text-muted)" }}>{c.subcategories.length} subcategorii • /{c.id}</div>
                    </div>
                  </button>
                  <Button variant="outline" onClick={() => del(c.id)} className="border-white/20 text-white hover:bg-white/5 h-9 px-3" title="Șterge"><Trash2 className="w-4 h-4" /></Button>
                </div>
                {open && (
                  <div className="border-t p-4 grid gap-4 fade-in" style={{ borderColor: "var(--amd-border)" }}>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white text-xs uppercase tracking-wider">Nume</Label>
                        <Input defaultValue={c.name} onBlur={(e) => update(c.id, { name: e.target.value })} className="amd-input h-10 mt-1" />
                      </div>
                      <div>
                        <Label className="text-white text-xs uppercase tracking-wider">Icon (Lucide)</Label>
                        <Input defaultValue={c.icon} onBlur={(e) => update(c.id, { icon: e.target.value })} className="amd-input h-10 mt-1" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-white text-xs uppercase tracking-wider">URL imagine</Label>
                        <Input defaultValue={c.image} onBlur={(e) => update(c.id, { image: e.target.value })} className="amd-input h-10 mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white text-xs uppercase tracking-wider">Subcategorii</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {c.subcategories.map((s) => (
                          <span key={s} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs" style={{ background: "var(--amd-surface-alt)", border: "1px solid var(--amd-border)", color: "var(--amd-text)" }}>
                            {s}
                            <button onClick={() => removeSub(c.id, s)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                        {c.subcategories.length === 0 && <span className="text-xs" style={{ color: "var(--amd-text-muted)" }}>Fără subcategorii încă.</span>}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Input value={newSub[c.id] || ""} onChange={(e) => setNewSub((s) => ({ ...s, [c.id]: e.target.value }))} placeholder="Subcategorie nouă" className="amd-input h-10" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(c.id); } }} />
                        <Button onClick={() => addSub(c.id)} className="amd-btn-primary gap-1 px-3"><Plus className="w-4 h-4" /> Adaugă</Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
